import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box } from '@eatclubapp/ec-component-library';
import { Modal } from '../../../Modal/Modal';
import { Heading } from '../../../Heading';
import { Divider } from '@eatclubapp/ec-component-library';
import { useMediaQuery, useTheme, Button } from '@mui/material';
import { PrimaryButton } from '../../../button/PrimaryButton';
import CurrencyInput from '../../../Inputs/CurrencyInput';
import { getAppConfig } from '../../../../constants/appConfig';
import { formatCurrency } from '../../../../utils/helpers';
import { devLog } from '../../../../utils';
import useStyles, { getModalStyles, getApplyButtonStyles } from './ApplyCreditModalStyles';
import Toast from '../../../Toast/Toast';
import {
  requestRestaurantRebatePayoutAction,
  fetchRestaurantRebateAction,
  clearRequestRestaurantRebatePayoutAction,
} from '../../../../actions/creditRebateAction';

const ApplyCreditModal = ({
  isOpen,
  onClose,
  availableCredit = '$30.00',
  restId,
  requestPayoutPending,
  requestPayoutSuccess,
  requestPayoutError,
  requestRestaurantRebatePayout,
  fetchRestaurantRebate,
  clearRequestRestaurantRebatePayout,
  onCreditApplied,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const appConfig = getAppConfig();
  const [creditAmount, setCreditAmount] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Track processed states to prevent duplicate handling
  const hasProcessedSuccess = useRef(false);
  const hasProcessedError = useRef(false);
  const previousRequestPayoutSuccess = useRef(false);
  const previousRequestPayoutError = useRef('');
  const toastTimeoutRef = useRef(null);

  // Parse available credit amount
  const availableCreditAmount = parseFloat(availableCredit.replace(/[^0-9.]/g, '')) || 0;
  const creditAmountNum = parseFloat(creditAmount) || 0;
  const hasError = creditAmount && creditAmountNum > availableCreditAmount;
  const remainingBalance = hasError
    ? availableCreditAmount
    : creditAmount && !isNaN(creditAmountNum) && creditAmountNum >= 0
      ? availableCreditAmount - creditAmountNum
      : availableCreditAmount;

  // Helper function to show toast
  const showToast = (message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    setToastOpen(true);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleCancel = () => {
    setCreditAmount('');
    onClose();
  };

  const handleCreditAmountChange = (e) => {
    setCreditAmount(e.target.value);
  };

  const handleApply = () => {
    if (!creditAmount || isNaN(creditAmountNum) || creditAmountNum <= 0) {
      showToast('Please enter a valid credit amount.');
      return;
    }

    if (hasError || !restId) {
      if (!restId) {
        showToast('Restaurant ID is missing. Please try again.');
      }
      return;
    }

    requestRestaurantRebatePayout(restId, creditAmountNum);
  };

  const handleToastClose = () => {
    setToastOpen(false);
    setToastMessage('');
  };

  // Handling submission success/error states
  useEffect(() => {
    const successChanged = requestPayoutSuccess && !previousRequestPayoutSuccess.current;

    if (successChanged && isOpen && !hasProcessedSuccess.current && creditAmountNum > 0) {
      hasProcessedSuccess.current = true;
      const formattedAmount = formatCurrency(creditAmountNum, true);
      setCreditAmount('');

      if (restId) {
        fetchRestaurantRebate(restId);
      }

      // Close the current modal first, then show the success modal
      onClose();

      // Show the credit applied modal after closing the current modal
      setTimeout(() => {
        if (onCreditApplied) {
          onCreditApplied(formattedAmount);
        }
        hasProcessedSuccess.current = false;
      }, 300);
    } else if (successChanged && creditAmountNum === 0) {
      clearRequestRestaurantRebatePayout();
      previousRequestPayoutSuccess.current = false;
    }

    // Update the previous value
    previousRequestPayoutSuccess.current = requestPayoutSuccess;
  }, [
    requestPayoutSuccess,
    isOpen,
    creditAmountNum,
    restId,
    clearRequestRestaurantRebatePayout,
    fetchRestaurantRebate,
    onClose,
  ]);

  // handles toast error state
  useEffect(() => {
    const errorChanged =
      requestPayoutError && requestPayoutError !== previousRequestPayoutError.current;

    if (errorChanged && isOpen && !hasProcessedError.current && requestPayoutError) {
      hasProcessedError.current = true;
      const errorMsg =
        typeof requestPayoutError === 'string'
          ? requestPayoutError
          : 'Failed to apply credit. Please try again.';

      setToastMessage(errorMsg);
      setToastOpen(true);
    }

    // Update the previous value
    previousRequestPayoutError.current = requestPayoutError || '';
    // Reset error processing flag when error is cleared
    if (!requestPayoutError) {
      hasProcessedError.current = false;
    }
  }, [requestPayoutError, isOpen, restId]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCreditAmount('');
      setToastOpen(false);
      setToastMessage('');
      hasProcessedSuccess.current = false;
      hasProcessedError.current = false;
      previousRequestPayoutSuccess.current = false;
      previousRequestPayoutError.current = '';
      clearRequestRestaurantRebatePayout();
    }
  }, [isOpen, clearRequestRestaurantRebatePayout]);

  const isSubmitDisabled =
    !creditAmount ||
    hasError ||
    requestPayoutPending ||
    requestPayoutSuccess ||
    creditAmountNum <= 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideFooter={true}
        style={getModalStyles(breakpointMdDown)}
      >
        <Box className={classes.applyCreditModalContent}>
          <Box className={classes.applyCreditModalMainContent}>
            <Heading className={classes.applyCreditModalTitle}>
              Apply credit to next settlement
            </Heading>
            <Box className={classes.applyCreditModalDescription}>
              This will reduce your EatClub commission on your next settlement
            </Box>

            <Box className={classes.applyCreditModalInputSection}>
              <Box className={classes.applyCreditModalInputRow}>
                <Box className={classes.applyCreditModalLabel}>Credit</Box>
                <Box className={classes.applyCreditModalInputContainer}>
                  <CurrencyInput
                    value={creditAmount}
                    onChange={handleCreditAmountChange}
                    placeholder='E.g. 100'
                    currencySymbol={appConfig.currencySymbol}
                    allowDecimals={true}
                    error={hasError}
                  />
                  {hasError && (
                    <Box className={classes.applyCreditModalErrorText}>
                      This amount exceeds your available credit balance
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />
            <Box className={classes.applyCreditModalBalanceRow}>
              <Box className={classes.applyCreditModalBalanceLabel}>Remaining balance</Box>
              <Box className={classes.applyCreditModalBalanceAmount}>
                {formatCurrency(remainingBalance, true)}
              </Box>
            </Box>
          </Box>

          <Box className={classes.applyCreditModalButtonContainer}>
            <Box className={classes.applyCreditModalButtonSection}>
              <Button
                className={classes.applyCreditModalCancelButton}
                onClick={handleCancel}
                variant='outlined'
                size='large'
              >
                Cancel
              </Button>
              <PrimaryButton
                style={getApplyButtonStyles()}
                onClick={handleApply}
                disabled={isSubmitDisabled}
              >
                {requestPayoutPending ? 'Applying...' : 'Apply credit'}
              </PrimaryButton>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Toast open={toastOpen} onClose={handleToastClose} message={toastMessage || ''} />
    </>
  );
};

ApplyCreditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availableCredit: PropTypes.string,
  restId: PropTypes.string,
  requestPayoutPending: PropTypes.bool,
  requestPayoutSuccess: PropTypes.bool,
  requestPayoutError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  requestRestaurantRebatePayout: PropTypes.func.isRequired,
  fetchRestaurantRebate: PropTypes.func.isRequired,
  clearRequestRestaurantRebatePayout: PropTypes.func.isRequired,
  onCreditApplied: PropTypes.func,
};

const mapStateToProps = (state) => ({
  restId: state.restaurantActive?.restaurant?.objectId,
  requestPayoutPending: state.creditRebate?.requestPayoutPending || false,
  requestPayoutSuccess: state.creditRebate?.requestPayoutSuccess || false,
  requestPayoutError: state.creditRebate?.requestPayoutError || '',
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      requestRestaurantRebatePayout: requestRestaurantRebatePayoutAction,
      fetchRestaurantRebate: fetchRestaurantRebateAction,
      clearRequestRestaurantRebatePayout: clearRequestRestaurantRebatePayoutAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(ApplyCreditModal);
